import { useEffect, useState } from 'react';
import { Video, CalendarDays, Flag, CheckSquare, Handshake, Landmark } from 'lucide-react';
import { parseAppDate } from '@/lib/datetime';

/* ---------- kind config (session / event / deadline / task) ---------- */
export const KIND_CONFIG = {
  session: { label: 'Session', icon: Video, dot: 'bg-primary', badge: 'bg-primary/15 text-primary border border-primary/40', route: (i) => `/app/sessions/${i.id}` },
  event: { label: 'Event', icon: CalendarDays, dot: 'bg-info', badge: 'bg-info/15 text-info border border-info/40', route: (i) => `/app/events/${i.id}` },
  booking: { label: '1:1 Booking', icon: Handshake, dot: 'bg-plum', badge: 'bg-plum/15 text-plum border border-plum/40', route: (i) => i.mentor_id ? `/app/mentors/${i.mentor_id}` : null },
  deadline: { label: 'Deadline', icon: Flag, dot: 'bg-destructive', badge: 'bg-destructive/15 text-destructive border border-destructive/30', route: (i) => i.course_id ? `/app/learn/${i.course_id}${i.lesson_id ? `?lesson=${encodeURIComponent(i.lesson_id)}` : ''}` : '/app/learn' },
  task: { label: 'Task', icon: CheckSquare, dot: 'bg-foreground', badge: 'bg-secondary text-foreground border border-border', route: () => null },
  capital_interview: { label: 'Capital Interview', icon: Landmark, dot: 'bg-teal', badge: 'bg-teal/15 text-teal border border-teal/40', route: () => '/app/capital/application' },
  capital_meeting: { label: 'Capital Meeting', icon: Landmark, dot: 'bg-teal', badge: 'bg-teal/15 text-teal border border-teal/40', route: () => '/app/capital/program' },
  capital_milestone: { label: 'Milestone Due', icon: Flag, dot: 'bg-gold', badge: 'bg-gold/15 text-gold border border-gold/40', route: () => '/app/capital/program' },
};
export const kindCfg = (k) => KIND_CONFIG[k] || KIND_CONFIG.task;

/**
 * Given a schedule feed, return the single item that is LIVE right now
 * (timed session/event whose [start, end] brackets now; end defaults to +1h),
 * or null. Used by the header "Live Now" chip.
 */
export const findLiveNow = (items = []) => {
  const now = Date.now();
  const live = (items || []).filter((i) => {
    if (i.kind !== 'session' && i.kind !== 'event') return false;
    if (!i.start || isAllDay(i.start)) return false;
    const start = parseAppDate(i.start).getTime();
    const end = effectiveEndMs(i.start, i.end);
    return now >= start && now <= end;
  });
  live.sort((a, b) => parseAppDate(a.start) - parseAppDate(b.start));
  return live[0] || null;
};

/* ---------- date helpers (naive ISO strings are treated as local time) ---------- */
const p2 = (n) => String(n).padStart(2, '0');
export const ymd = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${p2(x.getMonth() + 1)}-${p2(x.getDate())}`;
};
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; }; // Monday start
export const startOfMonth = (d) => { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; };
export const endOfMonth = (d) => { const x = new Date(d); x.setMonth(x.getMonth() + 1, 0); x.setHours(0, 0, 0, 0); return x; };
export const isSameDay = (a, b) => ymd(a) === ymd(b);
export const isAllDay = (iso) => !!iso && iso.length <= 10;

/** Effective end (ms) for a timed item — guards against bad data where the
 *  stored end is at/before the start (a mis-typed end time must never make an
 *  upcoming item look "ended"). Falls back to start + 1h. */
export const effectiveEndMs = (startIso, endIso, fallbackMs = 60 * 60 * 1000) => {
  if (!startIso || isAllDay(startIso)) return null;
  const start = parseAppDate(startIso)?.getTime();
  if (!start) return null;
  const end = endIso && !isAllDay(endIso) ? parseAppDate(endIso)?.getTime() : null;
  return end && end > start ? end : start + fallbackMs;
};

export const fmtTime = (iso) => {
  if (!iso) return '';
  if (isAllDay(iso)) return 'All day';
  return parseAppDate(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
};
export const fmtDayLabel = (d) => new Date(d).toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric' });

/* ---------- live countdown ---------- */
const fmtDelta = (ms) => {
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  if (days >= 1) return `in ${days} day${days > 1 ? 's' : ''}`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${p2(h)}:${p2(m)}:${p2(sec)}`;
};

/**
 * Returns { status: 'live'|'soon'|'upcoming'|'ended'|'none', label } for a timed item.
 * Ticks every second. Only meaningful for session/event (timed, future/ongoing).
 */
export const useCountdown = (startIso, endIso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!startIso || isAllDay(startIso)) return { status: 'none', label: '' };
  const start = parseAppDate(startIso).getTime();
  const end = effectiveEndMs(startIso, endIso);
  if (now >= start && now <= end) return { status: 'live', label: 'Live now' };
  if (now > end) return { status: 'ended', label: 'Ended' };
  const diff = start - now;
  const status = diff <= 60 * 60 * 1000 ? 'soon' : 'upcoming';
  return { status, label: `Starts ${fmtDelta(diff)}` };
};
